import type { Empresa, Usuario } from '../../modelos/Auth';

export interface ContextoIdentidadAnalitica {
  userId: string;
  userEmail?: string;
  userName?: string;
  userRole?: string;
  userStatus?: string;
  companyId?: string;
  companyRuc?: string;
  companyName?: string;
  companyConfigured: boolean;
  establecimientoId?: string;
  entorno?: 'demo' | 'produccion';
}

type EmpresaAnalitica = {
  ruc?: string;
  razonSocial?: string;
  nombreComercial?: string;
  ambienteSunat?: string;
};

const DEMO_RUC = '20000000000';

const derivarEntorno = (empresa: EmpresaAnalitica | null | undefined): 'demo' | 'produccion' | undefined => {
  if (!empresa) return undefined;
  if (empresa.ruc === DEMO_RUC) return 'demo';
  return 'produccion';
};

const obtenerNombreEmpresaSeguro = (empresa: EmpresaAnalitica | null | undefined): string | undefined => {
  const razonSocial = empresa?.razonSocial?.trim();
  const nombreComercial = empresa?.nombreComercial?.trim();
  return razonSocial || nombreComercial || undefined;
};

const obtenerNombreUsuarioSeguro = (user: Usuario): string | undefined => {
  const nombre = user.nombre?.trim();
  const apellido = user.apellido?.trim();
  const nombreCompleto = [nombre, apellido].filter(Boolean).join(' ').trim();
  return nombreCompleto || undefined;
};

const derivarEmpresaConfigurada = (
  companyId: string | undefined,
  empresa: EmpresaAnalitica | null | undefined,
): boolean => {
  if (!companyId) return false;
  const nombre = obtenerNombreEmpresaSeguro(empresa);
  const ruc = empresa?.ruc?.trim();
  return Boolean(nombre || ruc);
};

export function resolverContextoEmpresaAnalitica(
  companyId: string | undefined,
  empresa: EmpresaAnalitica | null | undefined,
): Pick<ContextoIdentidadAnalitica, 'companyId' | 'companyConfigured' | 'companyRuc' | 'companyName'> {
  return {
    companyId,
    companyRuc: empresa?.ruc?.trim() || undefined,
    companyName: obtenerNombreEmpresaSeguro(empresa),
    companyConfigured: derivarEmpresaConfigurada(companyId, empresa),
  };
}

export function resolverContextoIdentidadAnalitica(
  user: Usuario,
  empresaId: string | null,
  empresa: Empresa | null,
  establecimientoId: string | null,
): ContextoIdentidadAnalitica {
  const companyId = empresaId ?? undefined;
  const contextoEmpresa = resolverContextoEmpresaAnalitica(companyId, empresa);
  const entorno = derivarEntorno(empresa);

  return {
    userId: user.id,
    userEmail: user.email?.trim() || undefined,
    userName: obtenerNombreUsuarioSeguro(user),
    userStatus: user.esActivo ? 'activo' : 'inactivo',
    companyId: contextoEmpresa.companyId,
    companyRuc: contextoEmpresa.companyRuc,
    companyName: contextoEmpresa.companyName,
    companyConfigured: contextoEmpresa.companyConfigured,
    establecimientoId: establecimientoId ?? undefined,
    entorno,
  };
}
